/**
 * kiosk-service endpoints — sessions, transactions, attractions, photos,
 * cash-acceptor, info-kiosk lookups, pickup, and wristband print.
 */

import { EDC_BANK_CODES } from '@autopia/shared-types';
import {
  InitiatePurchaseRequestSchema,
  InitiateTopupRequestSchema,
  KioskDeviceSchema,
  KioskGroupPrintResultSchema,
  KioskIndividualPrintResultSchema,
  KioskPhotoItemSchema,
  KioskPickupPrintRequestSchema,
  KioskPickupResolutionSchema,
  KioskPickupResolveRequestSchema,
  KioskPrintRequestSchema,
  KioskPurchaseBatchRequestSchema,
  KioskPurchaseBatchResponseSchema,
  KioskReprintRequestSchema,
  KioskSessionStartRequestSchema,
  KioskSessionStartResponseSchema,
  KioskTransactionPollResultSchema,
  KioskTransactionResponseSchema,
  KioskTransactionStatusResponseSchema,
  type InitiatePurchaseRequest,
  type InitiateTopupRequest,
  type KioskGroupPrintResult,
  type KioskIndividualPrintResult,
  type KioskPaymentMethod,
  type KioskPhotoItem,
  type KioskPickupResolution,
  type KioskPurchaseBatchRequest,
  type KioskPurchaseBatchResponse,
  type KioskSessionStartResponse,
  type KioskSessionType,
  type KioskTransactionPollResult,
  type KioskTransactionResponse,
  type KioskTransactionStatusResponse,
} from '@autopia/api-contracts';
import { AttractionCreditItemSchema } from '@autopia/api-contracts/ticketing';
import { z } from 'zod';

import { env } from '../env';
import { analyticsApi, kioskApi, ticketingApi, unwrapAndParse } from './client';

const KioskPrintResultSchema = z.union([
  KioskGroupPrintResultSchema,
  KioskIndividualPrintResultSchema,
]);

export type {
  KioskPaymentMethod,
  KioskTransactionPollResult as TransactionPollResult,
  KioskGroupPrintResult as GroupPrintResult,
  KioskIndividualPrintResult as IndividualPrintResult,
  KioskPickupResolution as PickupResolution,
};

export type StartSessionResponse = KioskSessionStartResponse;
export type TopUpRequest = InitiateTopupRequest;
export type TopUpResponse = KioskTransactionResponse;
export type TransactionStatusResponse = KioskTransactionStatusResponse;

export interface Attraction {
  id: string;
  name: string;
  creditCost: number;
  imageUrl?: string;
  available: boolean;
}

export type PurchaseRequest = InitiatePurchaseRequest;

export interface CreditHistoryEntry {
  id: string;
  timestamp: string;
  description: string;
  amount: number;
  type: 'CREDIT' | 'DEBIT';
  balance: number;
}

export interface ZoneWaitTime {
  zoneId: string;
  zoneName: string;
  attractionId: string;
  attractionName: string;
  queueDepth: number;
  /** Estimated minutes — derived as queueDepth * 2 on the client */
  estimatedMinutes: number;
  minHeightCm: number | null;
  isOpen: boolean;
}

export interface ShowAttraction {
  id: string;
  name: string;
  location: string;
  durationMinutes: number;
  showTimes: string[];
}

export interface KioskConfig {
  id: string;
  name?: string;
  enabledPaymentMethods: KioskPaymentMethod[];
  edcBanks: string[];
  cashAcceptorEnabled: boolean;
}

export interface CashSessionEvent {
  type: 'bill_inserted' | 'total_updated' | 'session_complete' | 'overpayment_warning' | 'error';
  sessionId: string;
  orderId: string;
  billDenomination?: number;
  totalInserted: number;
  remaining: number;
  timestamp: string;
}

// ─── Sessions & Transactions ─────────────────────────────────────────────────

/** Start a kiosk session after wristband scan */
export async function startSession(
  kioskId: string,
  wristbandId: string,
  sessionType: KioskSessionType = 'BALANCE_CHECK',
): Promise<StartSessionResponse> {
  const payload = KioskSessionStartRequestSchema.parse({ wristbandId, sessionType });
  const { data } = await kioskApi.post(`/kiosks/${kioskId}/sessions/start`, payload);
  return unwrapAndParse(data, KioskSessionStartResponseSchema);
}

/** End the current kiosk session */
export async function endSession(kioskId: string, sessionId: string): Promise<void> {
  await kioskApi.post(`/kiosks/${kioskId}/sessions/${sessionId}/end`);
}

/** Initiate a credit top-up */
export async function initiateTopUp(
  kioskId: string,
  request: TopUpRequest,
): Promise<TopUpResponse> {
  const payload = InitiateTopupRequestSchema.parse(request);
  const { data } = await kioskApi.post(`/kiosks/${kioskId}/transactions/topup`, payload);
  return unwrapAndParse(data, KioskTransactionResponseSchema);
}

/** Poll for transaction status (QRIS / EDC payment completion) */
export async function getTransactionStatus(
  kioskId: string,
  transactionId: string,
): Promise<TransactionStatusResponse> {
  const { data } = await kioskApi.get(`/kiosks/${kioskId}/transactions/${transactionId}`);
  return unwrapAndParse(data, KioskTransactionStatusResponseSchema);
}

/** Fetch attractions that accept credits (public, served by ticketing-service) */
export async function getAttractions(): Promise<Attraction[]> {
  const { data } = await ticketingApi.get('/attractions/credits');
  const items = unwrapAndParse(data, z.array(AttractionCreditItemSchema));
  return items.map((a) => ({
    id: a.attractionId,
    name: a.name,
    creditCost: a.creditCost,
    imageUrl: a.thumbnailUrl ?? undefined,
    available: a.status === 'OPERATIONAL',
  }));
}

/** Purchase attraction tickets using wristband credits */
export async function purchaseAttractions(
  kioskId: string,
  request: PurchaseRequest,
): Promise<TopUpResponse> {
  const payload = InitiatePurchaseRequestSchema.parse(request);
  const { data } = await kioskApi.post(`/kiosks/${kioskId}/transactions/purchase`, payload);
  return unwrapAndParse(data, KioskTransactionResponseSchema);
}

/** Fetch credit transaction history for the kiosk session (filtered by wristband server-side) */
export async function getCreditHistory(
  kioskId: string,
  wristbandId: string,
): Promise<CreditHistoryEntry[]> {
  const { data } = await kioskApi.get<CreditHistoryEntry[]>(
    `/kiosks/${kioskId}/transactions`,
    { params: { wristbandId } },
  );
  return data;
}

/**
 * Poll a walk-up transaction for payment completion via the JWT-authenticated
 * walk-up controller. The kiosk JWT must be present (set by the kioskApi interceptor).
 */
export async function pollKioskTransaction(
  transactionId: string,
): Promise<KioskTransactionPollResult> {
  const { data } = await kioskApi.get(`/walk-up/purchase/${transactionId}/status`);
  return unwrapAndParse(data, KioskTransactionPollResultSchema);
}

// ─── Information Kiosk ───────────────────────────────────────────────────────

/**
 * Fetch current wait times.
 *
 * TODO (#548): this has never worked -- kiosk-service has no /capacity/zones
 * route (404 today), and capacity-service's real GET /capacity/zones returns
 * per-ZONE occupancy (%, LOW/MODERATE/HIGH/CRITICAL/FULL status), not
 * per-attraction queueDepth/minute-estimates/minHeightCm/isOpen -- those
 * fields don't exist anywhere server-side. Needs a product/data-model
 * decision (new endpoint or a real zone-occupancy-based UI) before this can
 * be fixed, not just a client repoint.
 */
export async function fetchWaitTimes(): Promise<ZoneWaitTime[]> {
  const { data } = await kioskApi.get<ZoneWaitTime[]>('/capacity/zones');
  return data.map((zone) => ({
    ...zone,
    estimatedMinutes: zone.queueDepth * 2,
  }));
}

/**
 * Fetch show-type attractions with today's schedule.
 *
 * TODO (#547): this has never worked -- kiosk-service has no /attractions
 * route (404 today), and no public endpoint anywhere returns showtimes data.
 * `Attraction` has no location/duration/showTimes fields; the only per-day
 * time-window data (TicketSchedule.dailyWindows) is locked behind
 * admin/ticket-schedules (JwtAuthGuard). Needs a design doc deciding what's
 * safe to expose publicly before a real endpoint can be built.
 */
export async function fetchShowSchedules(): Promise<ShowAttraction[]> {
  const { data } = await kioskApi.get<ShowAttraction[]>('/attractions');
  return data;
}

// ─── Photo Kiosk ─────────────────────────────────────────────────────────────

/** Fetch ride photos for a wristband (exit photo kiosk, served by analytics-service) */
export async function fetchWristbandPhotos(
  wristbandId: string,
): Promise<KioskPhotoItem[]> {
  const { data } = await analyticsApi.get('/photos/kiosk', {
    params: { wristbandId },
  });
  return unwrapAndParse(data, z.array(KioskPhotoItemSchema));
}

/** Purchase selected photos and get download link (served by analytics-service) */
export async function purchasePhotos(
  wristbandId: string,
  photoIds: string[],
  paymentMethod: KioskPurchaseBatchRequest['paymentMethod'],
): Promise<KioskPurchaseBatchResponse> {
  const payload = KioskPurchaseBatchRequestSchema.parse({ wristbandId, photoIds, paymentMethod });
  const { data } = await analyticsApi.post('/photos/kiosk/purchase-batch', payload);
  return unwrapAndParse(data, KioskPurchaseBatchResponseSchema);
}

// ─── Kiosk Config ────────────────────────────────────────────────────────────

export async function getKioskConfig(kioskId: string): Promise<KioskConfig> {
  const { data } = await kioskApi.get(`/kiosks/${kioskId}`);
  const raw = unwrapAndParse(data, KioskDeviceSchema);

  const paymentMethods: KioskPaymentMethod[] = (['QRIS', 'EDC', 'CASH'] as KioskPaymentMethod[])
    .filter((m) => raw.capabilities.includes(m));

  return {
    id: raw.id,
    enabledPaymentMethods: paymentMethods.length > 0 ? paymentMethods : ['QRIS', 'EDC', 'CASH'],
    edcBanks: [...EDC_BANK_CODES],
    cashAcceptorEnabled: raw.capabilities.includes('CASH'),
  };
}

// ─── Cash Acceptor SSE ───────────────────────────────────────────────────────

/** Open an SSE cash acceptor session. Returns the raw fetch Response for streaming. */
export async function startCashSession(
  kioskId: string,
  orderId: string,
  targetAmount: number,
): Promise<Response> {
  return fetch(`${env.kioskBaseUrl}/api/v1/cash-acceptor/sessions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Kiosk-Id': kioskId,
    },
    body: JSON.stringify({ orderId, targetAmount }),
  });
}

/** Terminate a cash acceptor session early (cancel). */
export async function endCashSession(sessionId: string): Promise<void> {
  await kioskApi.delete(`/cash-acceptor/sessions/${sessionId}`);
}

// ─── Pickup & Print ──────────────────────────────────────────────────────────

/** Resolve a scanned QR to an order snapshot (ORD- prefix or ticket QR). */
export async function kioskPickupResolve(
  qrContent: string,
  kioskId: string,
): Promise<KioskPickupResolution> {
  const payload = KioskPickupResolveRequestSchema.parse({ qrContent, kioskId });
  const { data } = await kioskApi.post('/kiosk/pickup/resolve', payload);
  return unwrapAndParse(data, KioskPickupResolutionSchema);
}

/** Print all wristbands for an individual order (mirror of group print-all). */
export async function kioskPickupPrint(
  orderNumber: string,
  kioskId: string,
): Promise<KioskGroupPrintResult> {
  const payload = KioskPickupPrintRequestSchema.parse({ orderNumber, kioskId });
  const { data } = await kioskApi.post('/kiosk/pickup/print', payload);
  return unwrapAndParse(data, KioskGroupPrintResultSchema);
}

/**
 * Staff-authorised reprint of a group booking.
 * Requires the server-side KIOSK_STAFF_PIN to match; returns 401 otherwise.
 * Bypasses the `printedAt` idempotency check — always forces a fresh print.
 */
export async function kioskReprint(
  bookingNumber: string,
  kioskId: string,
  staffPin: string,
  reason: string,
): Promise<KioskGroupPrintResult> {
  const payload = KioskReprintRequestSchema.parse({ bookingNumber, kioskId, staffPin, reason });
  const { data } = await kioskApi.post('/kiosk/print/reprint', payload);
  return unwrapAndParse(data, KioskGroupPrintResultSchema);
}

export async function kioskPrint(
  qrContent: string,
  kioskId: string,
): Promise<KioskGroupPrintResult | KioskIndividualPrintResult> {
  const payload = KioskPrintRequestSchema.parse({ qrContent, kioskId });
  const { data } = await kioskApi.post('/kiosk/print', payload);
  return unwrapAndParse(data, KioskPrintResultSchema);
}
